import type {
  ActingParty,
  PersonInvolvement,
  PersonRecordCreators,
} from "../types/involvement.js";
import type { PersonAction } from "../types/personActions.js";
import { mayArchivePerson } from "./mayArchivePerson.js";
import { mayEndGuestAccess } from "./mayEndGuestAccess.js";
import { personActionsFromCreation } from "./personActionsFromCreation.js";
import { personActionsFromMemberOrganisations } from "./personActionsFromMemberOrganisations.js";
import { personActionsFromOrganisationAdministration } from "./personActionsFromOrganisationAdministration.js";
import { personActionsFromOwnInvolvements } from "./personActionsFromOwnInvolvements.js";
import { personActionsNarrowedByAllocation } from "./personActionsNarrowedByAllocation.js";
import { personStateOf } from "./personStateOf.js";

type Involvement = Pick<
  PersonInvolvement,
  "kind" | "party" | "endedAt" | "expiresAt"
>;

/**
 * Everything one customer party, acting for one organisation in the roles
 * given, may ask of a person.
 *
 * The one composition of the rules: what having created one of the person's
 * records conveys, what organisation administration conveys, what the party's
 * involvements convey, and what its member organisations' involvements carry
 * up, narrowed by allocation, and then the actions derived from it. Anywhere
 * deciding what a customer party may ask of a person calls this rather than
 * composing the parts, so a further source of reach is one more input here and
 * cannot land after the narrowing it should be subject to.
 */
export const personActionsOf = (
  person: PersonRecordCreators & { archivedAt: Date | null },
  involvements: readonly Involvement[],
  party: ActingParty,
  now: Date = new Date(),
): PersonAction[] => {
  const conveyed = personActionsNarrowedByAllocation(
    [
      ...new Set([
        ...personActionsFromCreation(person, party),
        ...personActionsFromOrganisationAdministration(
          person,
          involvements,
          party,
          now,
        ),
        ...personActionsFromOwnInvolvements(involvements, party, now),
        ...personActionsFromMemberOrganisations(involvements, party, now),
      ]),
    ],
    involvements,
    now,
  );

  const derived: PersonAction[] = [];

  if (
    conveyed.includes("assess") &&
    personStateOf(person, involvements, now).kind === "active"
  ) {
    derived.push("start_assessment");
  }

  if (mayArchivePerson(person, involvements, party, now)) {
    derived.push("archive");
  }

  if (mayEndGuestAccess(conveyed)) {
    derived.push("end_guest_access");
  }

  return [...conveyed, ...derived];
};
